import { ChevronDown, ChevronRight, Folder } from "lucide-react";
import { Button } from "@/components/ui/NewButton";
import { useBundleTree, useBundleTreeActions } from "../bundleTreeStore";
import { GroupType, sectionChildCounts } from "../bundleTypes";
import { ViewEmailGroup } from "./ViewEmailGroup";
import { ViewFile } from "./ViewFile";

type ViewSectionProps = {
  groupId: string;
}

export const ViewSection = ({ groupId }: ViewSectionProps) => {
  const config = useBundleTree((s) => s.config);
  const isExpanded = useBundleTree((s) => s.expandedIds.has(groupId));
  const { toggleExpand: onToggleExpand } = useBundleTreeActions();
  const group = config.groups[groupId];
  const childIds = group?.children || [];
  const { docCount, subCount, isEmpty } = sectionChildCounts(config, groupId);

  return (
    <div className="border rounded-lg bg-white">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-t-lg border-b">
        <Button
          icon={isExpanded ? ChevronDown : ChevronRight}
          variant="bare"
          size="icon-md"
          onClick={() => onToggleExpand(groupId)}
          className="text-gray-500 hover:text-gray-700"
        />
        <Folder className="h-4 w-4 text-gray-400 shrink-0" />
        <span className="text-sm font-medium flex-1">
          {group.title}
          <span className="text-xs text-gray-400 font-normal ml-2">
            {docCount} doc{docCount !== 1 ? 's' : ''}
            {subCount > 0 && ` · ${subCount} section${subCount !== 1 ? 's' : ''}`}
          </span>
        </span>
      </div>
      {isExpanded && (isEmpty ? (
        <div className="px-3 py-4 text-xs text-gray-400 text-center">
          No documents in this group
        </div>
      ) : (
        <div className="px-5 py-3 space-y-1">
          {childIds.map((childId) => {
            const file = config.files[childId];
            if (file) return <ViewFile key={childId} file={file} />;
            if (config.groups[childId]?.groupType === GroupType.EMAIL) {
              return <ViewEmailGroup key={childId} groupId={childId} />;
            }
            return <ViewSection key={childId} groupId={childId} />;
          })}
        </div>
      ))}
    </div>
  );
};
